import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState } from 'react';
import useStyles from './styles';
import { useStore } from 'src/stores';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { track, events } from 'src/configs/amplitude';
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Checkbox, FormControlLabel, FormGroup, IconButton } from '@mui/material';
import { ALLOCATION_METHOD } from 'src/constants/utilities';
import { isBillShared, toShareRequest } from './billShareVisibility';

/**
 * Whether residents may see this cycle's bill.
 *
 * @param isForRecoveredBill
 *   The document being shared lives in bill-automation rather than the file service, so there is no
 *   fileId to carry (LB-5875). A bill allocated from the Bills page is always this: the allocation
 *   bill is created without a file, and the document is reached through its source bill instead.
 *   Residents already receive it that way; what was missing was any way to say they may.
 */
export const FileVisibilitySetting = observer(_ref => {
  var _property$allocations, _property$allocations2, _property$allocations3;
  let _ref$isForRecoveredBi = _ref.isForRecoveredBill,
    isForRecoveredBill = _ref$isForRecoveredBi === void 0 ? false : _ref$isForRecoveredBi;
  const store = useStore();
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Apply Billing';
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const applyBill = currentCompany.billingTab.applyBill || {};
  const billingCycleId = applyBill.billingCycleId;
  const property = store.homeDashboardStore.selectedPropertyId === 'all' ? store.homeDashboardStore.getCurrentCompanyProperties.find(p => p.id === applyBill.propertyId) || {} : store.homeDashboardStore.getCurrentProperty || {};
  const utilityId = applyBill.utilityId;
  const utility = (_property$allocations = property.allocations) === null || _property$allocations === void 0 ? void 0 : _property$allocations.utilityById.get(billingCycleId + utilityId);
  const allocationCycleId = applyBill.allocationCycleId;
  const billingCycleIds = (_property$allocations2 = property === null || property === void 0 || (_property$allocations3 = property.allocations) === null || _property$allocations3 === void 0 || (_property$allocations3 = _property$allocations3.billingCycleIds) === null || _property$allocations3 === void 0 ? void 0 : _property$allocations3.get(allocationCycleId)) !== null && _property$allocations2 !== void 0 ? _property$allocations2 : [];
  const utilitiesListNonSubmeter = billingCycleIds.flatMap(id => {
    var _property$allocations4, _cycle$utilityTables;
    const cycle = property === null || property === void 0 || (_property$allocations4 = property.allocations) === null || _property$allocations4 === void 0 || (_property$allocations4 = _property$allocations4.billingCycles) === null || _property$allocations4 === void 0 ? void 0 : _property$allocations4.get(id);
    const tables = (_cycle$utilityTables = cycle === null || cycle === void 0 ? void 0 : cycle.utilityTables) !== null && _cycle$utilityTables !== void 0 ? _cycle$utilityTables : [];
    return tables.map(row => {
      var _row$billingCycleId, _cycle$isDefaultBilli, _cycle$isHistoricalAl;
      return _objectSpread(_objectSpread({}, row), {}, {
        billingCycleId: (_row$billingCycleId = row.billingCycleId) !== null && _row$billingCycleId !== void 0 ? _row$billingCycleId : id,
        isDefaultBillingCycle: (_cycle$isDefaultBilli = cycle === null || cycle === void 0 ? void 0 : cycle.isDefaultBillingCycle) !== null && _cycle$isDefaultBilli !== void 0 ? _cycle$isDefaultBilli : false,
        isHistoricalAllocationCycle: (_cycle$isHistoricalAl = cycle === null || cycle === void 0 ? void 0 : cycle.isHistoricalAllocationCycle) !== null && _cycle$isHistoricalAl !== void 0 ? _cycle$isHistoricalAl : false
      });
    });
  }).sort((a, b) => Number(b.isDefaultBillingCycle) - Number(a.isDefaultBillingCycle)).filter(u => u.allocationMethod !== ALLOCATION_METHOD.BySubmeter);
  const hasUtilitySwitcher = utilitiesListNonSubmeter.length > 1;
  const getFileId = () => {
    var _property$allocations5, _property$allocations6, _utility$bill, _utility$bill2, _utility$bill3, _utility$bill4;
    return (_property$allocations5 = property.allocations) !== null && _property$allocations5 !== void 0 && (_property$allocations5 = _property$allocations5.tempFileData) !== null && _property$allocations5 !== void 0 && _property$allocations5.fileId ? (_property$allocations6 = property.allocations) === null || _property$allocations6 === void 0 || (_property$allocations6 = _property$allocations6.tempFileData) === null || _property$allocations6 === void 0 ? void 0 : _property$allocations6.fileId : utility !== null && utility !== void 0 && (_utility$bill = utility.bill) !== null && _utility$bill !== void 0 && _utility$bill.redactedFileId ? utility === null || utility === void 0 || (_utility$bill2 = utility.bill) === null || _utility$bill2 === void 0 ? void 0 : _utility$bill2.redactedFileId : utility !== null && utility !== void 0 && (_utility$bill3 = utility.bill) !== null && _utility$bill3 !== void 0 && _utility$bill3.fileId ? utility === null || utility === void 0 || (_utility$bill4 = utility.bill) === null || _utility$bill4 === void 0 ? void 0 : _utility$bill4.fileId : '';
  };
  const shareArgs = () => {
    var _property$allocations7;
    return {
      bill: utility === null || utility === void 0 ? void 0 : utility.bill,
      tempFileData: (_property$allocations7 = property.allocations) === null || _property$allocations7 === void 0 ? void 0 : _property$allocations7.tempFileData,
      isForRecoveredBill,
      fileId: getFileId()
    };
  };
  const isShared = isBillShared(shareArgs());

  // A recovered bill has no optimistic half: `isShared` comes off the bill, and the bill is only
  // updated once the request answers. Two clicks inside that window would both read the value
  // still on screen, compute the same target, and send it twice -- so the second could not undo
  // the first. Held shut until the answer is in, which is also the honest thing to show.
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isSaving = _useState2[0],
    setIsSaving = _useState2[1];
  const fileAttachedAsync = async value => {
    const request = toShareRequest(_objectSpread(_objectSpread({}, shareArgs()), {}, {
      isShared: value
    }));
    if (request) await store.allocations.getFileAttachedAsync.run(request);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: [classes.visibilitySetting, hasUtilitySwitcher && classes.visibilitySettingWithUtilitySwitcher].filter(Boolean).join(' ')
  }, /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: !!isShared,
      disabled: isSaving,
      onChange: async () => {
        const newValue = !isShared;
        track(newValue ? events.enabled('Bill Visible To Residents') : events.disabled('Bill Visible To Residents'), {
          page,
          utility: applyBill.utilityName
        });
        if (!isForRecoveredBill) {
          var _property$allocations8;
          (_property$allocations8 = property.allocations) === null || _property$allocations8 === void 0 || _property$allocations8.setTempFileData('isShared', newValue);
        }
        setIsSaving(true);
        try {
          var _utility$bill5;
          await fileAttachedAsync(newValue);
          utility === null || utility === void 0 || (_utility$bill5 = utility.bill) === null || _utility$bill5 === void 0 || _utility$bill5.changeIsSharedFile(newValue);
        } finally {
          // In a finally so a refused request gives the control back rather than leaving it
          // shut for as long as the modal stays open.
          setIsSaving(false);
        }
      }
    }),
    label: t('allocations.is_bill_visible')
  })), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: t('allocations.is_bill_visible_hint'),
    type: "grey"
  }, /*#__PURE__*/React.createElement(IconButton, null, /*#__PURE__*/React.createElement(Icon, {
    state: "info",
    type: "question"
  }))));
});